import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";

// Reusable scroll-triggered reveal container.
export const Reveal = ({ as = "div", children, delay = 0, y = 40, x = 0, className = "", ...rest }) => {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      initial={{ opacity: 0, y, x }}
      whileInView={{ opacity: 1, y: 0, x: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.9, delay, ease: [0.2, 0.7, 0.15, 1] }}
      className={className}
      {...rest}
    >
      {children}
    </MotionTag>
  );
};

// Dramatic fly-in reveal — used at section root for a page-level scroll-in effect.
export const FlyIn = ({ from = "bottom", children, className = "", delay = 0, distance = 90 }) => {
  const initial =
    from === "left"
      ? { opacity: 0, x: -distance, y: 0, rotate: -1 }
      : from === "right"
      ? { opacity: 0, x: distance, y: 0, rotate: 1 }
      : from === "top"
      ? { opacity: 0, x: 0, y: -distance }
      : { opacity: 0, x: 0, y: distance };
  return (
    <motion.div
      initial={initial}
      whileInView={{ opacity: 1, x: 0, y: 0, rotate: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1.05, delay, ease: [0.2, 0.7, 0.15, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Staggered children reveal
export const StaggerGroup = ({ children, className = "", stagger = 0.08 }) => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.2 }}
    variants={{
      hidden: {},
      show: { transition: { staggerChildren: stagger, delayChildren: 0.05 } },
    }}
    className={className}
  >
    {children}
  </motion.div>
);

export const StaggerItem = ({ children, className = "", y = 30 }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y },
      show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: [0.2, 0.7, 0.15, 1] } },
    }}
    className={className}
  >
    {children}
  </motion.div>
);

// Simple parallax wrapper — element translates on its scroll progress
export const Parallax = ({ children, offset = 60, className = "" }) => {
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [offset, -offset]);
  return (
    <motion.div ref={ref} style={{ y }} className={className}>
      {children}
    </motion.div>
  );
};

export { motion };
